import * as turf from "@turf/turf";
import type mapboxgl from "mapbox-gl";
import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { formatTranslation } from "../../../../shared/utils/translationUtils";
import {
  MEASURE_FILL_LAYER_ID,
  MEASURE_FIRST_POINT_LAYER_ID,
  MEASURE_LINE_LAYER_ID,
  MEASURE_POINTS_LAYER_ID,
  MEASURE_SOURCE_ID,
  type LngLatPoint,
  type Measurement,
} from "../MapControlsPanel.types";

interface MeasureToolProps {
  mapRef: mapboxgl.Map | null;
  isActive: boolean;
  onMeasurementChange?: (measurement: Measurement | null) => void;
}

/** Pixel radius within which a click counts as "back on the first point". */
const CLOSE_POLYGON_THRESHOLD_PX = 10;

const KM_TO_MILES = 0.621371;
const SQM_TO_ACRES = 0.000247105;
const SQM_TO_HECTARES = 0.0001;

const isMetric = (): boolean =>
  (localStorage.getItem("unitSystem") ?? "metric") === "metric";

export const measurementFor = (
  points: LngLatPoint[],
  closed: boolean
): Measurement | null => {
  if (points.length < 2) {
    return null;
  }

  const metric = isMetric();

  try {
    if (closed && points.length >= 3) {
      const ring = [
        ...points.map((p) => [p.lng, p.lat]),
        [points[0].lng, points[0].lat],
      ];
      const polygon = turf.polygon([ring]);
      const areaInMeters = turf.area(polygon);
      // turf.length returns kilometres by default.
      const perimeterInKm = turf.length(turf.lineString(ring));

      return {
        type: "area",
        value: metric
          ? areaInMeters * SQM_TO_HECTARES
          : areaInMeters * SQM_TO_ACRES,
        unit: metric ? "ha" : "ac",
        perimeter: metric ? perimeterInKm : perimeterInKm * KM_TO_MILES,
        perimeterUnit: metric ? "km" : "mi",
      };
    }

    const lengthInKm = turf.length(
      turf.lineString(points.map((p) => [p.lng, p.lat]))
    );

    return {
      type: "distance",
      value: metric ? lengthInKm : lengthInKm * KM_TO_MILES,
      unit: metric ? "km" : "mi",
    };
  } catch (error) {
    console.error("Error calculating measurement:", error);
    return null;
  }
};

const MeasureTool: React.FC<MeasureToolProps> = ({
  mapRef,
  isActive,
  onMeasurementChange,
}) => {
  const { t } = useTranslation();
  const [points, setPoints] = useState<LngLatPoint[]>([]);
  const [currentMousePos, setCurrentMousePos] = useState<LngLatPoint | null>(
    null
  );
  const [isClosed, setIsClosed] = useState(false);
  const [measurement, setMeasurement] = useState<Measurement | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);

  const tooltipRef = useRef<HTMLDivElement>(null);
  const pointsRef = useRef(points);
  const currentMousePosRef = useRef(currentMousePos);
  const isClosedRef = useRef(isClosed);
  const onMeasurementChangeRef = useRef(onMeasurementChange);

  useEffect(() => {
    pointsRef.current = points;
    currentMousePosRef.current = currentMousePos;
    isClosedRef.current = isClosed;
  }, [points, currentMousePos, isClosed]);

  useEffect(() => {
    onMeasurementChangeRef.current = onMeasurementChange;
  }, [onMeasurementChange]);

  const updateTooltipPosition = useCallback(
    (map: mapboxgl.Map, point: LngLatPoint) => {
      const pixel = map.project([point.lng, point.lat]);
      const rect = map.getCanvas().getBoundingClientRect();
      setTooltipPosition({ x: rect.left + pixel.x, y: rect.top + pixel.y });
    },
    []
  );

  const handleCopyMeasurement = useCallback(() => {
    if (!measurement) {
      return;
    }

    const area = `${measurement.value.toFixed(2)} ${measurement.unit}`;
    const text =
      measurement.type === "area" && measurement.perimeter !== undefined
        ? `${area}, ${measurement.perimeter.toFixed(2)} ${
            measurement.perimeterUnit ?? ""
          }`.trim()
        : area;

    navigator.clipboard
      .writeText(text)
      .then(() => {
        toast.success(
          formatTranslation(t("newTrial.mapControls.measurementCopied"))
        );
      })
      .catch(() => {
        toast.error(formatTranslation(t("newTrial.mapControls.copyFailed")));
      });
  }, [measurement, t]);

  const addMapLayers = useCallback((map: mapboxgl.Map) => {
    [
      MEASURE_FIRST_POINT_LAYER_ID,
      MEASURE_POINTS_LAYER_ID,
      MEASURE_FILL_LAYER_ID,
      MEASURE_LINE_LAYER_ID,
    ].forEach((layerId) => {
      if (map.getLayer(layerId)) {
        map.removeLayer(layerId);
      }
    });
    if (map.getSource(MEASURE_SOURCE_ID)) {
      map.removeSource(MEASURE_SOURCE_ID);
    }

    map.addSource(MEASURE_SOURCE_ID, {
      type: "geojson",
      data: { type: "FeatureCollection", features: [] },
    });

    map.addLayer({
      id: MEASURE_FILL_LAYER_ID,
      type: "fill",
      source: MEASURE_SOURCE_ID,
      paint: { "fill-color": "#31B4F2", "fill-opacity": 0.2 },
      filter: ["==", "$type", "Polygon"],
    });

    map.addLayer({
      id: MEASURE_LINE_LAYER_ID,
      type: "line",
      source: MEASURE_SOURCE_ID,
      paint: {
        "line-color": "#31B4F2",
        "line-width": 2,
        "line-dasharray": [2, 2],
      },
      filter: ["==", "$type", "LineString"],
    });

    map.addLayer({
      id: MEASURE_POINTS_LAYER_ID,
      type: "circle",
      source: MEASURE_SOURCE_ID,
      paint: {
        "circle-radius": 5,
        "circle-color": "#FFFFFF",
        "circle-stroke-width": 2,
        "circle-stroke-color": "#31B4F2",
      },
      filter: ["==", "$type", "Point"],
    });

    map.addLayer({
      id: MEASURE_FIRST_POINT_LAYER_ID,
      type: "circle",
      source: MEASURE_SOURCE_ID,
      paint: {
        "circle-radius": 7,
        "circle-color": "#31B4F2",
        "circle-stroke-width": 2,
        "circle-stroke-color": "#FFFFFF",
      },
      filter: [
        "all",
        ["==", "$type", "Point"],
        ["==", ["get", "isFirst"], true],
      ],
    });
  }, []);

  const removeMapLayers = useCallback((map: mapboxgl.Map) => {
    [
      MEASURE_FIRST_POINT_LAYER_ID,
      MEASURE_POINTS_LAYER_ID,
      MEASURE_FILL_LAYER_ID,
      MEASURE_LINE_LAYER_ID,
    ].forEach((layerId) => {
      if (map.getLayer(layerId)) {
        map.removeLayer(layerId);
      }
    });
    if (map.getSource(MEASURE_SOURCE_ID)) {
      map.removeSource(MEASURE_SOURCE_ID);
    }
  }, []);

  const updateMapSource = useCallback(
    (
      map: mapboxgl.Map,
      pts: LngLatPoint[],
      mousePos: LngLatPoint | null,
      closed: boolean
    ) => {
      const source = map.getSource(MEASURE_SOURCE_ID);
      if (source?.type !== "geojson") {
        return;
      }

      const features: GeoJSON.Feature[] = pts.map((point, index) => ({
        type: "Feature",
        properties: { isFirst: index === 0 },
        geometry: { type: "Point", coordinates: [point.lng, point.lat] },
      }));

      if (closed && pts.length >= 3) {
        features.push({
          type: "Feature",
          properties: {},
          geometry: {
            type: "Polygon",
            coordinates: [
              [...pts.map((p) => [p.lng, p.lat]), [pts[0].lng, pts[0].lat]],
            ],
          },
        });
      } else {
        const lineCoords =
          mousePos && pts.length > 0
            ? [...pts.map((p) => [p.lng, p.lat]), [mousePos.lng, mousePos.lat]]
            : pts.map((p) => [p.lng, p.lat]);

        if (lineCoords.length >= 2) {
          features.push({
            type: "Feature",
            properties: {},
            geometry: { type: "LineString", coordinates: lineCoords },
          });
        }
      }

      source.setData({ type: "FeatureCollection", features });
    },
    []
  );

  const isNearFirstPoint = useCallback(
    (
      clickPos: mapboxgl.LngLat,
      firstPoint: LngLatPoint,
      map: mapboxgl.Map
    ): boolean => {
      const firstPixel = map.project([firstPoint.lng, firstPoint.lat]);
      const clickPixel = map.project(clickPos);
      const distance = Math.hypot(
        firstPixel.x - clickPixel.x,
        firstPixel.y - clickPixel.y
      );
      return distance < CLOSE_POLYGON_THRESHOLD_PX;
    },
    []
  );

  const handleMapClick = useCallback(
    (e: mapboxgl.MapMouseEvent) => {
      if (isClosedRef.current || !mapRef) {
        return;
      }

      const newPoint: LngLatPoint = { lng: e.lngLat.lng, lat: e.lngLat.lat };

      setPoints((prevPoints) => {
        const closesPolygon =
          prevPoints.length >= 3 &&
          isNearFirstPoint(e.lngLat, prevPoints[0], mapRef);

        if (closesPolygon) {
          setIsClosed(true);
          const closedMeasurement = measurementFor(prevPoints, true);
          setMeasurement(closedMeasurement);
          onMeasurementChangeRef.current?.(closedMeasurement);

          if (closedMeasurement) {
            const centre = turf.center(
              turf.polygon([
                [
                  ...prevPoints.map((p) => [p.lng, p.lat]),
                  [prevPoints[0].lng, prevPoints[0].lat],
                ],
              ])
            );
            updateTooltipPosition(mapRef, {
              lng: centre.geometry.coordinates[0],
              lat: centre.geometry.coordinates[1],
            });
          }
          return prevPoints;
        }

        const updatedPoints = [...prevPoints, newPoint];
        const openMeasurement = measurementFor(updatedPoints, false);
        setMeasurement(openMeasurement);
        onMeasurementChangeRef.current?.(openMeasurement);
        if (openMeasurement) {
          updateTooltipPosition(mapRef, newPoint);
        }
        return updatedPoints;
      });
    },
    [mapRef, isNearFirstPoint, updateTooltipPosition]
  );

  const handleMouseMove = useCallback((e: mapboxgl.MapMouseEvent) => {
    if (isClosedRef.current) {
      return;
    }
    setCurrentMousePos({ lng: e.lngLat.lng, lat: e.lngLat.lat });
  }, []);

  const clearMeasurement = useCallback(() => {
    setPoints([]);
    setCurrentMousePos(null);
    setIsClosed(false);
    setMeasurement(null);
    setTooltipPosition(null);
    onMeasurementChangeRef.current?.(null);
  }, []);

  useEffect(() => {
    if (!mapRef || !isActive) {
      clearMeasurement();
      if (mapRef) {
        try {
          removeMapLayers(mapRef);
        } catch {
          // Map may already be torn down.
        }
      }
      return;
    }

    if (mapRef.isStyleLoaded()) {
      addMapLayers(mapRef);
    }

    // Covers both the pending initial style load and the base layer selector
    // swapping the whole style, which drops our layers.
    const handleStyleData = (): void => {
      if (mapRef.isStyleLoaded() && !mapRef.getSource(MEASURE_SOURCE_ID)) {
        addMapLayers(mapRef);
        if (pointsRef.current.length > 0) {
          updateMapSource(
            mapRef,
            pointsRef.current,
            currentMousePosRef.current,
            isClosedRef.current
          );
        }
      }
    };

    mapRef.on("styledata", handleStyleData);
    mapRef.on("click", handleMapClick);
    mapRef.on("mousemove", handleMouseMove);
    mapRef.getCanvas().style.cursor = "crosshair";

    return () => {
      try {
        mapRef.off("styledata", handleStyleData);
        mapRef.off("click", handleMapClick);
        mapRef.off("mousemove", handleMouseMove);
        const canvas = mapRef.getCanvas();
        if (canvas) {
          canvas.style.cursor = "";
        }
        removeMapLayers(mapRef);
      } catch {
        // Expected while the map is unmounting.
      }
    };
  }, [
    mapRef,
    isActive,
    addMapLayers,
    removeMapLayers,
    handleMapClick,
    handleMouseMove,
    updateMapSource,
    clearMeasurement,
  ]);

  useEffect(() => {
    if (mapRef && isActive) {
      updateMapSource(mapRef, points, currentMousePos, isClosed);
    }
  }, [mapRef, isActive, points, currentMousePos, isClosed, updateMapSource]);

  useEffect(() => {
    if (tooltipPosition) {
      tooltipRef.current?.style.setProperty(
        "--tooltip-x",
        `${tooltipPosition.x}px`
      );
      tooltipRef.current?.style.setProperty(
        "--tooltip-y",
        `${tooltipPosition.y - 40}px`
      );
    }
  }, [tooltipPosition]);

  if (!isActive || !measurement || !tooltipPosition) {
    return null;
  }

  const tooltip = (
    <div ref={tooltipRef} className="measure-tooltip-marker">
      <span className="measure-tooltip-value">
        {measurement.value.toFixed(2)} {measurement.unit}
        {measurement.type === "area" && measurement.perimeter !== undefined && (
          <span className="measure-tooltip-perimeter">
            {formatTranslation(t("newTrial.mapControls.perimeter"))}{" "}
            {measurement.perimeter.toFixed(2)} {measurement.perimeterUnit}
          </span>
        )}
      </span>
      <button
        type="button"
        className="measure-tooltip-copy"
        onClick={handleCopyMeasurement}
        aria-label={formatTranslation(
          t("newTrial.mapControls.copyMeasurement")
        )}
        data-testid="measure-copy-button"
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <rect
            x="4"
            y="4"
            width="8"
            height="9"
            rx="1"
            stroke="currentColor"
            strokeWidth="1.2"
            fill="none"
          />
          <path
            d="M3 10H2C1.44772 10 1 9.55228 1 9V2C1 1.44772 1.44772 1 2 1H8C8.55228 1 9 1.44772 9 2V3"
            stroke="currentColor"
            strokeWidth="1.2"
            fill="none"
          />
        </svg>
      </button>
    </div>
  );

  return ReactDOM.createPortal(tooltip, document.body);
};

export default MeasureTool;
